import React, { useState } from 'react';
import { can, isAdminOrPlatform } from '../lib/access';
import { CheckCircle, ChevronDown, ChevronUp, Lightbulb, ArrowRight, Info, Scale, AlertTriangle, ShieldCheck, Search, Copy, Check, ExternalLink, Globe, MessageCircle } from 'lucide-react';

// Auto-linkify URLs in text and resolve [W#] citation tokens against web_links
const Linkified = ({ text, webLinks }) => {
  if (!text) return null;
  const hasWebLinks = Array.isArray(webLinks) && webLinks.length > 0;
  // Split on either plain URLs OR [W1]/[W2]/[W3] citation markers
  const tokenRegex = /(https?:\/\/[^\s,)]+|\[W\d+\])/g;
  const parts = text.split(tokenRegex);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        // URL
        if (/^https?:\/\//i.test(part)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 hover:text-sky-800 underline underline-offset-2 inline-flex items-center gap-0.5"
            >
              {part.length > 50 ? part.slice(0, 48) + '...' : part}
              <ExternalLink size={10} className="flex-shrink-0" />
            </a>
          );
        }
        // [W#] citation
        const citeMatch = part.match(/^\[W(\d+)\]$/);
        if (citeMatch) {
          const idx = parseInt(citeMatch[1], 10) - 1;
          const link = hasWebLinks && idx >= 0 && idx < webLinks.length ? webLinks[idx] : null;
          if (link && link.url) {
            return (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`inline-web-cite-${idx}`}
                className="inline-flex items-center align-super text-[10px] font-semibold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded px-1.5 py-0.5 mx-0.5 transition-colors"
                title={link.title || link.host || link.url}
              >
                W{idx + 1}
              </a>
            );
          }
          // No matching source — render as muted badge instead of raw text
          return (
            <span key={i} className="inline-flex items-center align-super text-[10px] font-medium text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 mx-0.5">
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const confidenceStyle = (conf, mode) => {
  if (mode === 'operational' || (!mode && conf >= 0.75)) return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'High', dot: 'bg-emerald-500' };
  if (mode === 'diagnostic' || (!mode && conf >= 0.5)) return { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', label: 'Medium', dot: 'bg-amber-500' };
  return { bg: 'bg-red-50 border-red-200', text: 'text-red-600', label: 'Low', dot: 'bg-red-500' };
};

// Validation card — renders ONLY from the retrieval-stage `grounding` payload
// (matched_terms + raw source_quote). Never renders the generated answer text.
const ValidationCard = ({ grounding }) => {
  const status = grounding?.validation_status || 'ungrounded';
  const matchedTerms = Array.isArray(grounding?.matched_terms) ? grounding.matched_terms : [];
  const basis = grounding?.match_basis;
  const sources = Array.isArray(grounding?.sources) ? grounding.sources : [];

  const statusBadge = {
    grounded: { cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: 'Grounded in source' },
    partial_match: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Partially grounded' },
    ungrounded: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Ungrounded' },
  }[status] || { cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', label: status };

  const basisLabel = basis ? `${basis} match` : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="validation-card">
      <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Lightbulb size={15} className="text-amber-500" />
          <span className="text-sm font-semibold text-gray-800">Source facts</span>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${statusBadge.cls}`}
          data-testid="validation-status"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot}`} />
          {statusBadge.label}
        </span>
      </div>

      <div className="px-5 py-4">
        {status === 'ungrounded' ? (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="validation-ungrounded">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-800">No source rule found</div>
              <div className="text-sm text-amber-700 leading-relaxed">
                This answer was generated from general reasoning and is not backed by a specific internal document. Verify before acting.
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Matched terms as chips + match basis */}
            {matchedTerms.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Matched terms</span>
                  {basisLabel && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full">
                      {basisLabel}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="matched-terms">
                  {matchedTerms.map((term, i) => (
                    <span
                      key={i}
                      className="text-[13px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1 rounded-lg"
                    >
                      {term}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Partial-match advisory */}
            {status === 'partial_match' && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5 mb-4" data-testid="validation-partial">
                <Info size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-[13px] text-amber-800 leading-relaxed">
                  This source addresses part of the query — verify the rest manually.
                </span>
              </div>
            )}

            {/* Raw source rule quote(s) */}
            {sources.length > 0 && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Source rule</div>
                <div className="space-y-3">
                  {sources.map((src, i) => (
                    <div
                      key={i}
                      className="relative bg-slate-50 border border-slate-200 border-l-4 border-l-sky-500 rounded-lg px-4 py-3.5"
                      data-testid="source-quote"
                    >
                      {/* Serif + italic typography marks this as quoted original text,
                          visually distinct from the generated answer. */}
                      <p className="text-[14px] leading-relaxed text-slate-700 italic font-serif">
                        &ldquo;{src.source_quote}&rdquo;
                      </p>
                      <div className="mt-3 flex items-center flex-wrap gap-2 text-xs text-gray-500">
                        {src.doc_id && (
                          <span className="font-mono text-[11px] font-bold text-sky-700 bg-sky-100 px-2 py-0.5 rounded">
                            {src.doc_id}
                          </span>
                        )}
                        {src.doc_title && (
                          <>
                            <span className="text-gray-300">&bull;</span>
                            <span>{src.doc_title}</span>
                          </>
                        )}
                        {src.clause_ref && (
                          <>
                            <span className="text-gray-300">&bull;</span>
                            <span>{src.clause_ref}</span>
                          </>
                        )}
                        {src.last_updated && (
                          <>
                            <span className="text-gray-300">&bull;</span>
                            <span>Updated {src.last_updated}</span>
                          </>
                        )}
                      </div>
                      {(src.badges || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {src.badges.map(badge => (
                            <span
                              key={badge.id}
                              className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
                              style={{ borderColor: badge.color, color: badge.color, backgroundColor: `${badge.color}12` }}
                            >
                              {badge.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-500">
                        {src.version_label && <span>Version {src.version_label}</span>}
                        {src.source_published_at && <span>Published {src.source_published_at}</span>}
                        {src.effective_from && <span>Effective from {src.effective_from}</span>}
                        {src.effective_until && <span>Effective until {src.effective_until}</span>}
                        {src.last_reviewed_at && <span>Reviewed {src.last_reviewed_at}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


const DecisionResponse = ({
  decision,
  queryFocus,
  userRole,
  currentUser,
  isWebResponse,
  isCombinedResponse,
  onAskFloorSpace,
  confidenceMode,
  confidenceFactors,
}) => {
  const [actionsOpen, setActionsOpen] = useState(true);
  const [logicOpen, setLogicOpen] = useState(false);
  const [causesOpen, setCausesOpen] = useState(true);
  const [factorsOpen, setFactorsOpen] = useState(false);
  const [terminationOpen, setTerminationOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  if (!decision || !decision.direct_answer) return null;

  const isAdmin = isAdminOrPlatform(currentUser || { role: userRole });
  const canInspectEvidence = can(currentUser || { role: userRole }, 'can_manage_knowledge');
  const conf = confidenceStyle(decision.confidence, confidenceMode);
  const evidenceFactors = confidenceFactors || decision.confidence_factors || {};
  const factorRows = [
    ['source_authority', 'Source authority', 20],
    ['structural_completeness', 'Actionable completeness', 25],
    ['concept_coverage', 'Question coverage', 25],
    ['corroboration', 'Support from other sources', 15],
    ['guard_pass', 'Safety checks passed', 15],
  ];
  const hasActions = decision.primary_action?.length > 0;
  const hasLogic = decision.key_logic?.length > 0;
  const grounding = decision.grounding;
  const hasGrounding = !!(grounding && grounding.validation_status);
  const hasCauses = decision.possible_causes?.length > 0;
  const hasFactors = decision.determining_factors?.length > 0;
  const hasFinalRule = !!decision.final_rule;
  const hasTermination = decision.termination_conditions?.length > 0;
  const hasFalseAssumption = !!decision.false_assumption;
  const hasNotes = decision.notes?.length > 0;
  const externalOnly = isWebResponse && !isCombinedResponse;

  const isRuleResponse = hasFactors || hasFinalRule || hasTermination;
  const isDiagnostic = queryFocus === 'DIAGNOSTIC';

  const handleCopy = () => {
    const parts = [decision.direct_answer];
    if (hasCauses) {
      parts.push('\nPossible Causes:');
      decision.possible_causes.forEach((c, i) => parts.push(`${i + 1}. ${c}`));
    }
    if (hasActions) {
      parts.push('\nWhat to do:');
      decision.primary_action.forEach((a, i) => parts.push(`${i + 1}. ${a}`));
    }
    if (hasLogic) {
      parts.push('\nWhy:');
      decision.key_logic.forEach(k => parts.push(`- ${k}`));
    }
    navigator.clipboard.writeText(parts.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-4" data-testid="decision-response">
      {/* A. Short Answer Card */}
      <div className={`rounded-xl overflow-hidden ${
        externalOnly
          ? 'border border-indigo-200/60 bg-indigo-50/30'
          : `border-2 ${conf.bg}`
      }`} data-testid="resolution-card">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {isDiagnostic ? (
                <Search size={18} className="text-violet-600" />
              ) : isRuleResponse ? (
                <Scale size={18} className="text-sky-700" />
              ) : (
                <CheckCircle size={18} className="text-emerald-600" />
              )}
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {isDiagnostic ? 'Diagnosis' : isRuleResponse ? 'Decision' : 'Resolution'}
              </span>
              {queryFocus && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">{queryFocus}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5" data-testid="confidence-display">
                <span className={`w-2 h-2 rounded-full ${externalOnly ? 'bg-indigo-400' : conf.dot}`} />
                <span className={`text-xs font-medium ${externalOnly ? 'text-indigo-600' : conf.text}`}>
                  {isCombinedResponse
                    ? <span className="flex items-center gap-1"><Globe size={11} />Internal + web</span>
                    : isWebResponse
                      ? <span className="flex items-center gap-1"><Globe size={11} />External guidance</span>
                    : <>{conf.label} evidence{isAdmin && ` · ${Math.round(decision.confidence * 100)}%`}</>
                  }
                </span>
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-gray-500 hover:bg-white/60 hover:text-gray-700 transition-colors border border-transparent hover:border-gray-200"
                data-testid="copy-answer-btn"
                title="Copy answer"
              >
                {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </div>
          <div
            className={`${externalOnly
              ? 'text-sm text-gray-800 leading-relaxed whitespace-pre-wrap'
              : 'text-base font-semibold text-gray-900 leading-snug'}`}
            data-testid="direct-answer"
          >
            {!externalOnly && (
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Qflow interpretation / application
              </div>
            )}
            <Linkified text={decision.direct_answer} webLinks={decision.web_links} />
          </div>
        </div>
      </div>

      {!externalOnly && canInspectEvidence && Object.keys(evidenceFactors).length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="evidence-explanation">
          <button
            type="button"
            onClick={() => setEvidenceOpen(current => !current)}
            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Info size={14} /> Why this evidence level?
            </span>
            {evidenceOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {evidenceOpen && (
            <div className="space-y-3 border-t px-4 py-3">
              <p className="text-xs leading-relaxed text-slate-600">
                This measures how strongly the available source material supports the response. It is not the probability that the answer is correct.
              </p>
              {factorRows.map(([key, label, weight]) => {
                const score = Math.round((Number(evidenceFactors[key]) || 0) * 100);
                return (
                  <div key={key}>
                    <div className="mb-1 flex items-center justify-between text-[11px] text-slate-600">
                      <span>{label} <span className="text-slate-400">({weight}% weight)</span></span>
                      <strong>{score}%</strong>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-sky-500" style={{ width: `${score}%` }} />
                    </div>
                  </div>
                );
              })}
              <p className="text-[10px] text-slate-400">
                High evidence starts at 75%; medium at 50%. A single uncorroborated source is capped below full certainty.
              </p>
            </div>
          )}
        </div>
      )}

      {/* False Assumption */}
      {hasFalseAssumption && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="false-assumption">
          <AlertTriangle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{decision.false_assumption}</p>
        </div>
      )}

      {/* DIAGNOSTIC: Possible Causes */}
      {isDiagnostic && hasCauses && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/30 overflow-hidden" data-testid="possible-causes-panel">
          <button
            onClick={() => setCausesOpen(!causesOpen)}
            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-violet-50 transition-colors"
            data-testid="toggle-causes"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={15} className="text-violet-600" />
              <span className="text-sm font-semibold text-gray-800">Possible Causes</span>
              <span className="text-xs text-gray-400">({decision.possible_causes.length})</span>
            </div>
            {causesOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {causesOpen && (
            <div className="px-5 pb-4 border-t border-violet-100">
              <ol className="space-y-3 pt-3">
                {decision.possible_causes.map((cause, i) => (
                  <li key={i} className="flex items-start gap-2.5" data-testid={`cause-${i}`}>
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed"><Linkified text={cause} /></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Actions — expanded by default */}
      {hasActions && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="action-panel">
          <button
            onClick={() => setActionsOpen(!actionsOpen)}
            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            data-testid="toggle-actions"
          >
            <div className="flex items-center gap-2">
              <ArrowRight size={15} className="text-sky-600" />
              <span className="text-sm font-semibold text-gray-800">{isDiagnostic ? 'What to investigate / do' : 'What to do'}</span>
              <span className="text-xs text-gray-400">({decision.primary_action.length})</span>
            </div>
            {actionsOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {actionsOpen && (
            <div className="px-5 pb-4 border-t border-gray-100">
              <ol className="space-y-2.5 pt-3">
                {decision.primary_action.map((action, i) => (
                  <li key={i} className="flex items-start gap-2.5" data-testid={`action-${i}`}>
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed"><Linkified text={action} /></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Why this is correct — retrieval-grounded Validation card (preferred) */}
      {hasGrounding && !isDiagnostic && (
        <ValidationCard grounding={grounding} />
      )}

      {/* Legacy Why panel — only when no structured grounding payload exists */}
      {!hasGrounding && hasLogic && !isDiagnostic && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="key-logic-panel">
          {isRuleResponse ? (
            <div className="px-5 py-3.5">
              <div className="flex items-center gap-2 mb-2.5">
                <Lightbulb size={15} className="text-amber-500" />
                <span className="text-sm font-semibold text-gray-800">Why</span>
              </div>
              <ul className="space-y-1.5">
                {decision.key_logic.map((rule, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-testid={`logic-${i}`}>
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    <span><Linkified text={rule} /></span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <button
                onClick={() => setLogicOpen(!logicOpen)}
                className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid="toggle-logic"
              >
                <div className="flex items-center gap-2">
                  <Lightbulb size={15} className="text-amber-500" />
                  <span className="text-sm font-semibold text-gray-800">Why this is correct</span>
                  <span className="text-xs text-gray-400">({decision.key_logic.length})</span>
                </div>
                {logicOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>
              {logicOpen && (
                <div className="px-5 pb-4 border-t border-gray-100">
                  <ul className="space-y-2 pt-3">
                    {decision.key_logic.map((rule, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-testid={`logic-${i}`}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                        <span><Linkified text={rule} /></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Determining Factors */}
      {hasFactors && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="factors-panel">
          <button onClick={() => setFactorsOpen(!factorsOpen)} className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid="toggle-factors">
            <div className="flex items-center gap-2">
              <Scale size={15} className="text-sky-600" />
              <span className="text-sm font-semibold text-gray-800">Determining Factors</span>
            </div>
            {factorsOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {factorsOpen && (
            <div className="px-5 pb-4 border-t border-gray-100">
              <ul className="space-y-2 pt-3">
                {decision.determining_factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-testid={`factor-${i}`}>
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Termination Conditions */}
      {hasTermination && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="termination-panel">
          <button onClick={() => setTerminationOpen(!terminationOpen)} className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid="toggle-termination">
            <div className="flex items-center gap-2">
              <ShieldCheck size={15} className="text-gray-500" />
              <span className="text-sm font-semibold text-gray-800">What would change this outcome</span>
            </div>
            {terminationOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {terminationOpen && (
            <div className="px-5 pb-4 border-t border-gray-100">
              <ul className="space-y-2 pt-3">
                {decision.termination_conditions.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-testid={`termination-${i}`}>
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Final Rule */}
      {hasFinalRule && (
        <div className="px-4 py-3 rounded-lg bg-slate-800 text-white" data-testid="final-rule">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={15} className="text-slate-300 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium leading-relaxed">{decision.final_rule}</p>
          </div>
        </div>
      )}

      {/* Notes */}
      {hasNotes && (
        <div className="px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="notes-panel">
          {decision.notes.map((note, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-500">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}

      {/* Web mode: external source links */}
      {isWebResponse && decision.web_links?.length > 0 && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 overflow-hidden" data-testid="web-links-panel">
          <div className="px-4 py-2.5">
            <div className="flex items-center gap-2 mb-2">
              <Globe size={13} className="text-indigo-500" />
              <span className="text-xs font-medium text-indigo-700">Sources</span>
            </div>
            <div className="space-y-1.5">
              {decision.web_links.map((link, i) => {
                let website = link.host || '';
                try { website = website || new URL(link.url).hostname.replace(/^www\./, ''); } catch {}
                return (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-2 text-xs text-sky-600 hover:text-sky-800 transition-colors"
                    data-testid={`web-link-${i}`}>
                    <ExternalLink size={11} className="mt-0.5 flex-shrink-0" />
                    <span className="min-w-0">
                      <span className="block underline underline-offset-2">{link.title}</span>
                      <span className="block text-[11px] text-slate-500 no-underline">{website}</span>
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Validation note — single message only, no duplicates */}
      {isCombinedResponse ? (
        <p className="text-xs text-slate-500 text-center" data-testid="confidence-note">
          Internal guidance remains authoritative; web context is additional and clearly sourced.
        </p>
      ) : isWebResponse ? (
        <p className="text-xs text-indigo-500 text-center" data-testid="confidence-note">
          Externally sourced — not internally validated. If unsure, verify via Floor Space.
        </p>
      ) : !isWebResponse && decision.confidence < 0.7 && decision.confidence > 0 ? (
        <p className="text-xs text-gray-400 text-center" data-testid="confidence-note">
          {isAdmin
            ? `Confidence: ${Math.round(decision.confidence * 100)}% — if unsure, verify via Floor Space or Floor Leaders.`
            : 'If unsure, verify via Floor Space or Floor Leaders.'
          }
        </p>
      ) : null}

      {/* Admin-only: Validation warnings — hidden in Web Mode (web answers don't follow the
          structured-response validation rules that produce these warnings). */}
      {isAdmin && !externalOnly && decision.validation && !decision.validation.passed && (
        <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="validation-warnings">
          <div className="flex items-start gap-2 text-xs text-red-600">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>Validation issues: {decision.validation.issues.join(', ')}</span>
          </div>
        </div>
      )}

      {/* Web mode: Ask on Floor Space CTA */}
      {isWebResponse && (
        <button
          onClick={onAskFloorSpace}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium"
          data-testid="web-floor-space-btn"
        >
          <MessageCircle size={15} />
          <span>Ask on Floor Space</span>
        </button>
      )}

    </div>
  );
};

export default DecisionResponse;
